import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import { convertImageToWebp, convertWebpToImage } from '@/utils/image-resource-kit';
import { computerImageTransform, deleteExtraInfo } from '@/utils/layer-kit';
import {
    LayerType,
    type IBackgroundLayerProps,
    type IExportBehavior,
    type IImageProps,
    type ILayerBehavior,
    type ILayerProps,
    type ISize,
} from '@/version-types';
import { EdgelessBackgroundLayer } from './edgeless-layer';
import { renderLayerToPPT } from './render/render-to-ppt';
import { renderLayerToStage } from './render/render-to-stage';

function getBehavior(identity: string): ILayerBehavior {
    return {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessBackgroundLayer(layer.id, getFramework(identity));
        },

        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IBackgroundLayerProps;

            // 兼容线上旧配方
            if (data.backgroundColor === 'transparent') {
                data.backgroundColor = '#00000000';
            }

            data.url = base64Kit.getCode(convertImageToWebp(data.url));
        },

        async onBeforeSaveAsync(layer: ILayerProps) {
            const data = layer.data as IImageProps;
            data.url = convertWebpToImage(data.url);
            //容错处理
            data.imageTransform.heightRatio = data.imageTransform.heightRatio || 1;
            data.imageTransform.widthRatio = data.imageTransform.widthRatio || 1;
            data.imageTransform.offsetX = data.imageTransform.offsetX || 0;
            data.imageTransform.offsetY = data.imageTransform.offsetY || 0;
            deleteExtraInfo(layer);
            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
        },

        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, resultSize, identity);
        },

        async savePPTLayerAsync(slide, layer, stageWidth, stageHeight, watermark) {
            return renderLayerToPPT(slide, layer, stageWidth, stageHeight, identity, watermark);
        },

        checkDisableCollision(): boolean {
            return true;
        },
        checkDisableInGroup(): boolean {
            return true;
        },
        checkDisableActive(): boolean {
            return true;
        },
        checkDisableFlip() {
            return true;
        },
        isBackgroundLayer() {
            return true;
        },
        adjustHeight(layer: ILayerProps, newHeight: number) {
            const data = layer.data as IBackgroundLayerProps;
            const newWidth = layer.width;
            layer.height = newHeight;
            if (!data.url) return;
            computerImageTransform(newWidth, newHeight, data);
        },
        adjustWidth(layer: ILayerProps, newWidth: number) {
            const data = layer.data as IBackgroundLayerProps;
            const newHeight = layer.height;
            layer.width = newWidth;
            if (!data.url) return;
            computerImageTransform(newWidth, newHeight, data);
        },
        adjustResize(layer: ILayerProps, newWidth: number, newHeight: number) {
            const data = layer.data as IBackgroundLayerProps;
            layer.height = newHeight;
            layer.width = newWidth;
            if (!data.url) return;
            computerImageTransform(newWidth, newHeight, data);
        },
        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.background);
        },

        getMenuOverlapIcon(layer) {
            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            const data = layer.data as IBackgroundLayerProps;
            const fallbackUrl = data.dynamicInfo?.screenshotUrl || data.url;
            return (
                <ResourceThumbnail
                    resource={resource}
                    fallbackUrl={fallbackUrl ? base64Kit.getLocalUrl(fallbackUrl) : null}
                />
            );
        },
    };
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.BACKGROUND_LAYER,
    getBehavior,
};

export default exportBehavior;
